import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AppButton, AppField, AppInput } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { IconCamera, IconCheck, IconError, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { createLead } from "../api";
import { evaluateGeofence, formatCoord } from "../geofence";
import { useGpsCapture, useOfflineQueue, useOnlineStatus } from "../hooks";
import { OsmMap } from "../../../shared/maps/OsmMap";
import { osmExternalUrl } from "../../../shared/maps/osm";
import { isValidTzMobile, normalizeTzPhone } from "../../../shared/phone";
import { syncPhoto } from "../sync";
import {
  enqueue,
  getLocalLead,
  getPhotoFile,
  newLocalId,
  normalisePhotos,
  registerPhotoFile,
  upsertLocalLead,
  type LeadPhotos,
  type LocalLeadRecord,
  type PhotoUploadState,
  type PhotoWhich,
} from "../store";
import type { GeofenceState, LeadCreateRequest } from "../types";

/**
 * S1 — Lead capture (handoff TASK-002, s1-lead-capture-* boards).
 * Full name → first/last split, NIDA (20 digits), phone (+255…), ward,
 * address, GPS capture widget (in_range / out_of_range / acquiring),
 * customer + house photo inputs. Offline → queue mutation + offline banner.
 * Photos (TASK-007b): uploaded to POST /leads/:id/photos after the lead is
 * created — failures never fail lead creation; the success view offers
 * per-photo inline retry. Offline → photo uploads queue behind the create.
 */

interface FormErrors {
  fullName?: string;
  nida?: string;
  phone?: string;
  ward?: string;
  address?: string;
}

const NAME_SPLIT = /\s+/;

function splitFullName(fullName: string): { first_name: string; last_name: string } | null {
  const parts = fullName.trim().split(NAME_SPLIT).filter(Boolean);
  if (parts.length < 2) return null;
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

function nidaDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function validate(form: {
  fullName: string;
  nida: string;
  phone: string;
  ward: string;
  address: string;
}): FormErrors {
  const errors: FormErrors = {};
  if (!splitFullName(form.fullName)) {
    errors.fullName = form.fullName.trim()
      ? "Enter first and last name"
      : "Full name is required";
  }
  if (nidaDigits(form.nida).length !== 20) {
    errors.nida = "NIDA must be 20 digits — re-check the ID card";
  }
  if (!isValidTzMobile(form.phone)) {
    errors.phone = "Use international format: +255 754 286 910";
  }
  if (!form.ward.trim()) errors.ward = "Ward is required";
  if (!form.address.trim()) errors.address = "Address is required";
  return errors;
}

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function S1LeadCapture() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const online = useOnlineStatus();
  const { refresh: refreshQueue } = useOfflineQueue(online);
  const gps = useGpsCapture();
  useFieldHeader("New Lead", `Customer registration — ${gps.fenceLabel}`);
  useFieldJourney({ flow: "leads", step: 0, backTo: "/field/leads" });

  const [form, setForm] = useState({ fullName: "", nida: "", phone: "", ward: "", address: "" });
  const [photos, setPhotos] = useState<LeadPhotos>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdLeadId, setCreatedLeadId] = useState<number | null>(null);
  const [uploads, setUploads] = useState<Partial<Record<PhotoWhich, PhotoUploadState>>>({});
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [manualConfirmed, setManualConfirmed] = useState(false);
  const draftPrefilled = useRef(false);
  // Stable id for this form instance — doubles as the local record id, so
  // photo Files registered at selection time line up with the saved record.
  const formId = useRef<string>(newLocalId());

  // Prefill from a saved draft (S4 "Return to Form" → /field/leads/new?draft=<localId>)
  useEffect(() => {
    if (draftPrefilled.current) return;
    const draftId = searchParams.get("draft");
    if (!draftId) return;
    const record = getLocalLead(draftId);
    if (record?.formDraft) {
      const d = record.formDraft;
      setForm({
        fullName: `${d.first_name} ${d.last_name}`.trim(),
        nida: d.national_id ?? "",
        phone: d.phone ?? "",
        ward: d.ward ?? "",
        address: d.address ?? "",
      });
      formId.current = draftId;
      // Photo Files survive only in-session (registry). Keep the metadata for
      // photos whose File is still available; otherwise the rep recaptures.
      const restored = normalisePhotos(record.photos) ?? {};
      setPhotos({
        customer:
          restored.customer &&
          (restored.customer.state === "uploaded" || getPhotoFile(draftId, "customer"))
            ? restored.customer
            : undefined,
        house:
          restored.house && (restored.house.state === "uploaded" || getPhotoFile(draftId, "house"))
            ? restored.house
            : undefined,
      });
      draftPrefilled.current = true;
    }
  }, [searchParams]);

  // Auto-start GPS acquisition on mount (design default shows captured coords).
  useEffect(() => {
    gps.capture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manualLatNumber = Number(manualLat);
  const manualLngNumber = Number(manualLng);
  const manualCoordsValid =
    manualLat.trim() !== "" &&
    manualLng.trim() !== "" &&
    Number.isFinite(manualLatNumber) &&
    Number.isFinite(manualLngNumber) &&
    manualLatNumber >= -90 &&
    manualLatNumber <= 90 &&
    manualLngNumber >= -180 &&
    manualLngNumber <= 180;
  const manualGeofence = manualCoordsValid
    ? evaluateGeofence(manualLatNumber, manualLngNumber)
    : null;
  const usingManualCoords = manualConfirmed && manualGeofence !== null;
  const allowManualCoords =
    gps.state.phase === "error" ||
    (gps.state.phase === "captured" && gps.state.geofence.state === "out_of_range");
  const geofenceState: GeofenceState =
    usingManualCoords ? manualGeofence.state
    : gps.state.phase === "captured" ? gps.state.geofence.state
    : gps.state.phase === "acquiring" ? "acquiring"
    : "unknown";
  const blocked = geofenceState === "out_of_range";
  const acquiring = geofenceState === "acquiring";
  const submissionBlocked = blocked && !usingManualCoords;

  const buildPayload = (): LeadCreateRequest | null => {
    const name = splitFullName(form.fullName);
    if (!name) return null;
    return {
      ...name,
      phone: normalizeTzPhone(form.phone),
      national_id: form.nida.trim(),
      ward: form.ward.trim(),
      address: form.address.trim(),
      ...(usingManualCoords
        ? {
            gps_lat: manualLatNumber,
            gps_lng: manualLngNumber,
            geofence_state: manualGeofence.state,
          }
        : gps.state.phase === "captured"
        ? {
            gps_lat: gps.state.lat,
            gps_lng: gps.state.lng,
            gps_accuracy_m: gps.state.accuracyM ?? undefined,
            geofence_state: gps.state.geofence.state,
          }
        : { geofence_state: "unknown" as GeofenceState }),
      source: "field_rep",
    };
  };

  /** Upload one photo; failures surface inline and never fail the lead. */
  const startPhotoUpload = (which: PhotoWhich, leadId: number) => {
    const file = getPhotoFile(formId.current, which);
    if (!file || !photos[which]) return;
    setUploads((u) => ({ ...u, [which]: "uploading" }));
    syncPhoto(formId.current, leadId, which, file)
      .then(() => setUploads((u) => ({ ...u, [which]: "uploaded" })))
      .catch(() => setUploads((u) => ({ ...u, [which]: "failed" })));
  };

  const leadMutation = useMutation({
    mutationFn: (payload: LeadCreateRequest) => createLead(payload),
    onSuccess: (res) => {
      upsertLocalLead({
        localId: formId.current,
        serverId: res.lead.id,
        name: `${res.lead.first_name} ${res.lead.last_name}`,
        phone: res.lead.phone,
        ward: res.lead.ward ?? undefined,
        status: "synced",
        createdAt: new Date().toISOString(),
        photos,
      });
      // Photo uploads ride after lead creation — best-effort, inline retry.
      const initial: Partial<Record<PhotoWhich, PhotoUploadState>> = {};
      (["customer", "house"] as PhotoWhich[]).forEach((w) => {
        if (photos[w] && getPhotoFile(formId.current, w)) initial[w] = "pending";
      });
      setUploads(initial);
      setCreatedLeadId(res.lead.id);
      (["customer", "house"] as PhotoWhich[]).forEach((w) => {
        if (initial[w]) startPhotoUpload(w, res.lead.id);
      });
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : "Failed to save lead — retry");
    },
  });

  const handleSubmit = () => {
    setSubmitError(null);
    const fieldErrors = validate(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    const payload = buildPayload();
    if (!payload) return;
    if (submissionBlocked || acquiring) return;
    if (gps.state.phase === "error" && !usingManualCoords) {
      setSubmitError("GPS unavailable. Enter valid coordinates and confirm them before continuing.");
      return;
    }

    if (!online) {
      // Offline: queue the create + photo uploads (they run after the lead
      // syncs — sync.ts resolves localId → serverId), then continue.
      const localId = formId.current;
      upsertLocalLead({
        localId,
        name: `${payload.first_name} ${payload.last_name}`,
        phone: payload.phone,
        ward: payload.ward,
        status: "pending-sync",
        createdAt: new Date().toISOString(),
        formDraft: payload,
        photos,
      });
      enqueue({
        queueId: crypto.randomUUID(),
        kind: "create-lead",
        localId,
        payload,
        createdAt: new Date().toISOString(),
      });
      (["customer", "house"] as PhotoWhich[]).forEach((w) => {
        if (photos[w] && getPhotoFile(localId, w)) {
          enqueue({
            queueId: crypto.randomUUID(),
            kind: "photo-upload",
            localId,
            leadRef: localId, // resolved to the server id after create-lead syncs
            photoType: w,
            createdAt: new Date().toISOString(),
          });
        }
      });
      refreshQueue();
      setDraftSavedAt(nowHHMM());
      navigate(`/field/leads/${localId}/duplicates`);
      return;
    }
    leadMutation.mutate(payload);
  };

  const handleSaveDraft = () => {
    // Geofence-blocked draft: stored on device only, submission not attempted.
    const payload = buildPayload();
    const name = splitFullName(form.fullName);
    const record: LocalLeadRecord = {
      localId: formId.current,
      name: name ? `${name.first_name} ${name.last_name}` : form.fullName.trim() || "Unnamed draft",
      phone: payload?.phone,
      ward: payload?.ward,
      status: "draft",
      createdAt: new Date().toISOString(),
      formDraft: payload ?? undefined,
      photos,
    };
    upsertLocalLead(record);
    refreshQueue();
    setDraftSavedAt(nowHHMM());
    navigate("/field/leads");
  };

  const set = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handlePhotoPick = (which: PhotoWhich) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPhotos((p) => ({ ...p, [which]: undefined }));
      return;
    }
    registerPhotoFile(formId.current, which, file);
    setPhotos((p) => ({ ...p, [which]: { name: file.name, state: "pending" } }));
  };

  const busy = leadMutation.isPending;
  const primaryLabel = useMemo(() => {
    if (busy) return "Checking duplicates…";
    if (!online) return "Save offline & Continue";
    return "Save & Check Duplicates";
  }, [busy, online]);

  // ── Success view: lead saved; photo uploads run with per-photo retry ──────
  if (createdLeadId !== null) {
    const photoRows = (["customer", "house"] as PhotoWhich[]).filter((w) => photos[w]);
    return (
      <>
        <div className="pg-state">
          <span className="state-icon success">
            <IconCheck size={40} />
          </span>
          <h2>Lead saved</h2>
          <p>Duplicate check is next — photos finish uploading in the background.</p>
        </div>

        {photoRows.length > 0 ? (
          <div className="pg-summary">
            {photoRows.map((w) => {
              const state = uploads[w] ?? "pending";
              return (
                <div className="row" key={w}>
                  <span className="k">{w === "customer" ? "Customer photo" : "House photo"}</span>
                  <span className="v">
                    {state === "uploaded" ? (
                      <span className="pg-badge success">
                        <IconCheck size={12} /> Uploaded
                      </span>
                    ) : state === "failed" ? (
                      <>
                        <span className="pg-badge error">
                          <IconError size={12} /> Upload failed
                        </span>{" "}
                        <button
                          type="button"
                          className="pg-linklike"
                          onClick={() => startPhotoUpload(w, createdLeadId)}
                        >
                          Retry
                        </button>
                      </>
                    ) : (
                      <span className="pg-badge info">
                        <IconSpinner size={12} /> Uploading…
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}

        <div className="pg-actions">
          <AppButton
            type="button"
            onClick={() => navigate(`/field/leads/${createdLeadId}/duplicates`)}
          >
            Continue to Duplicate Check
          </AppButton>
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate("/field/leads")}
          >
            Back to Leads
          </AppButton>
        </div>
      </>
    );
  }

  return (
    <>
      <AppField label="Full name" error={errors.fullName}>
        <AppInput
          value={form.fullName}
          onChange={set("fullName")}
          placeholder="e.g. Neema Jackson Mushi"
          className={errors.fullName ? "pg-input error" : undefined}
          autoComplete="name"
        />
      </AppField>

      <AppField label="National ID (NIDA)" error={errors.nida}>
        <AppInput
          value={form.nida}
          onChange={set("nida")}
          placeholder="19920314-22115-33445-16"
          inputMode="numeric"
          className={errors.nida ? "pg-input error" : undefined}
        />
      </AppField>

      <AppField label="Phone" error={errors.phone}>
        <AppInput
          value={form.phone}
          onChange={set("phone")}
          placeholder="+255 754 286 910"
          inputMode="tel"
          autoComplete="tel"
          className={errors.phone ? "pg-input error" : undefined}
        />
      </AppField>

      <AppField label="Ward" error={errors.ward}>
        <AppInput
          value={form.ward}
          onChange={set("ward")}
          placeholder="e.g. Mwananyamala"
          className={errors.ward ? "pg-input error" : undefined}
        />
      </AppField>

      <AppField label="Address" error={errors.address}>
        <AppInput
          value={form.address}
          onChange={set("address")}
          placeholder="Plot, block, street"
          className={errors.address ? "pg-input error" : undefined}
        />
      </AppField>

      {/* GPS capture widget — 3 geofence states (handoff s1-geofence-variants) */}
      <div
        className={`pg-gps-widget${
          geofenceState === "out_of_range" ? " out-of-range" : gps.state.phase === "error" ? " error" : ""
        }`}
      >
        <div>
          <div className="gps-label">GPS capture</div>
          {gps.state.phase === "captured" ? (
            <>
              <div className="gps-coords">{formatCoord(gps.state.lat, gps.state.lng)}</div>
              <div className="gps-meta">
                {gps.state.accuracyM !== null ? `Accuracy ±${gps.state.accuracyM} m` : "Accuracy unknown"}
              </div>
            </>
          ) : gps.state.phase === "acquiring" ? (
            <>
              <div className="gps-coords">— acquiring…</div>
              <div className="gps-meta">Hold still — locking satellites</div>
            </>
          ) : gps.state.phase === "error" ? (
            <>
              <div className="gps-coords">GPS unavailable</div>
              <div className="gps-meta">{gps.state.message}</div>
            </>
          ) : (
            <div className="gps-coords">Not captured</div>
          )}
        </div>
        {gps.state.phase === "captured" ? (
          gps.state.geofence.state === "in_range" ? (
            <span className="pg-badge success">
              <IconCheck size={12} /> In range
            </span>
          ) : (
            <span className="pg-badge warning">
              <IconWarning size={12} /> Out of range
            </span>
          )
        ) : gps.state.phase === "acquiring" ? (
          <span className="pg-badge info">
            <IconSpinner size={12} /> Acquiring
          </span>
        ) : (
          <AppButton variant="secondary" type="button" onClick={gps.capture}>
            {gps.state.phase === "error" ? "Retry GPS" : "Capture GPS"}
          </AppButton>
        )}
      </div>

      {gps.state.phase === "captured" ? (
        <div style={{ marginTop: 12 }}>
          <OsmMap
            pins={[
              {
                lat: gps.state.lat,
                lng: gps.state.lng,
                popup: "Lead location",
                accuracyM: gps.state.accuracyM,
              },
            ]}
            height={200}
            zoom={16}
            center={[gps.state.lat, gps.state.lng]}
          />
          <p className="pg-text-muted" style={{ marginTop: 8, fontSize: "0.8rem" }}>
            <a
              href={osmExternalUrl(gps.state.lat, gps.state.lng)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in OpenStreetMap ↗
            </a>
          </p>
        </div>
      ) : null}

      {blocked && gps.state.phase === "captured" ? (
        <div className="pg-geofence-warning" role="alert">
          <IconWarning size={14} />
          {usingManualCoords && manualGeofence
            ? `${Math.max(0, Math.round(manualGeofence.distanceFromFenceM))} m outside ${gps.fenceLabel} service fence — saved as out of range using confirmed manual coordinates`
            : `${Math.max(0, Math.round(gps.state.geofence.distanceFromFenceM))} m outside ${gps.fenceLabel} service fence — enter confirmed manual coordinates or save a draft`}
        </div>
      ) : null}

      {gps.state.phase === "error" ? (
        <div className="pg-geofence-warning" role="alert">
          <IconWarning size={14} />
          GPS unavailable — enter the location manually and confirm the coordinates.
        </div>
      ) : null}

      {allowManualCoords ? (
        <div style={{ marginTop: 12 }}>
          <div className="pg-two-col-form">
            <AppField label="Manual latitude">
              <AppInput
                type="number"
                step="any"
                min="-90"
                max="90"
                value={manualLat}
                onChange={(e) => {
                  setManualLat(e.target.value);
                  setManualConfirmed(false);
                }}
                placeholder="-6.7712"
              />
            </AppField>
            <AppField label="Manual longitude">
              <AppInput
                type="number"
                step="any"
                min="-180"
                max="180"
                value={manualLng}
                onChange={(e) => {
                  setManualLng(e.target.value);
                  setManualConfirmed(false);
                }}
                placeholder="39.2399"
              />
            </AppField>
          </div>
          {(manualLat || manualLng) && !manualCoordsValid ? (
            <div className="pg-inline-error" role="alert">
              Enter a latitude from -90 to 90 and longitude from -180 to 180.
            </div>
          ) : null}
          {manualGeofence ? (
            <p className="pg-text-muted">
              Manual location: {manualGeofence.state === "in_range" ? "In range" : "Out of range"}.
            </p>
          ) : null}
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <input
              type="checkbox"
              checked={manualConfirmed}
              onChange={(e) => setManualConfirmed(e.target.checked)}
              disabled={!manualCoordsValid}
            />
            I confirm these coordinates
          </label>
        </div>
      ) : null}

      <div className="pg-photo-row">
        <label className={`pg-photo-tile${photos.customer ? " captured" : ""}`}>
          {photos.customer ? <IconCheck size={20} /> : <IconCamera size={20} />}
          {photos.customer?.name ?? "Customer photo"}
          <input
            type="file"
            accept="image/*"
            capture="user"
            onChange={handlePhotoPick("customer")}
          />
        </label>
        <label className={`pg-photo-tile${photos.house ? " captured" : ""}`}>
          {photos.house ? <IconCheck size={20} /> : <IconCamera size={20} />}
          {photos.house?.name ?? "House photo"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoPick("house")}
          />
        </label>
      </div>

      {draftSavedAt ? (
        <div className="pg-draft-note" role="status">
          <IconCheck size={14} /> Draft saved on device · {draftSavedAt}
        </div>
      ) : null}

      {submitError ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} /> {submitError}
        </div>
      ) : null}

      <div className="pg-actions">
        {submissionBlocked ? (
          <AppButton variant="secondary" type="button" onClick={handleSaveDraft}>
            Save draft (submission blocked)
          </AppButton>
        ) : (
          <AppButton type="button" onClick={handleSubmit} disabled={busy || acquiring}>
            {busy ? <IconSpinner size={14} /> : null} {primaryLabel}
          </AppButton>
        )}
      </div>
    </>
  );
}
